// Writing festival preferences one answer at a time.
//
// The onboarding action writes a whole profile, seeds every artist on the bill
// and opens a crew in one call, which is right at the end of a setup pass and
// wrong for a control somebody just touched. These write the profile field that
// changed and leave the seeding for an explicit build.
//
// A profile written before goals could be ranked carries one value. Both shapes
// are kept: the first ranked goal is also written to the single field, so an
// older reader and a newer one agree.

import { useCallback } from 'react';
import { useApp } from './store.tsx';
import { mergePreferences } from './profile-defaults.ts';
import type { PreferencePatch } from './profile-defaults.ts';

export { BLANK, mergePreferences, readProfileForSolver } from './profile-defaults.ts';
export type { PreferencePatch, StoredProfile } from './profile-defaults.ts';

export function usePreferences() {
  const { state, actions, storage } = useApp();
  const stored = state.profiles.find((p) => p.userId === state.userId);

  const savePreferences = useCallback(
    async (patch: PreferencePatch) => {
      await storage.putOne('profiles', mergePreferences(stored, patch, state.userId));
      await actions.reload();
    },
    [stored, state.userId, storage, actions],
  );

  // Runs the seeding pass the old setup screen ran at its end, using whatever
  // the profile now holds rather than a form's contents.
  const seedFromPreferences = useCallback(
    async (crewName?: string) => {
      const profile = state.profiles.find((p) => p.userId === state.userId);
      await actions.completeOnboarding({
        displayName:
          state.members.find((m) => m.userId === state.userId)?.displayName || 'You',
        subgenreWeights: (profile?.subgenreWeights ?? {}) as Record<string, 'love' | 'like' | 'no'>,
        goalPriority: profile?.goalPriority,
        goalPriorities: profile?.goalPriorities,
        staminaCurve: profile?.staminaCurve,
        alignmentIntent: profile?.alignmentIntent,
        workspaceName: crewName ?? state.workspace?.name ?? '',
      });
    },
    [state, actions],
  );

  const nameCrew = useCallback(
    async (name: string) => {
      await seedFromPreferences(name);
    },
    [seedFromPreferences],
  );

  return { profile: stored, savePreferences, seedFromPreferences, nameCrew };
}
